import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FaBitcoin,
  FaPlus,
  FaEdit,
  FaTrash,
  FaSync,
  FaSearch,
  FaTimes,
  FaCheck,
  FaArrowUp,
  FaArrowDown
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../core/api/client';
import './CryptoPortfolio.css';

const CryptoPortfolio = () => {
  const [holdings, setHoldings] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isOwner, setIsOwner] = useState(false);

  // Add form
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [amount, setAmount] = useState('');

  // Edit
  const [editingId, setEditingId] = useState(null);
  const [editAmount, setEditAmount] = useState('');

  const fetchPortfolio = useCallback(async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    try {
      const response = await api.get('/crypto/portfolio');
      setHoldings(response.data.holdings);
      setTotalValue(response.data.total_value_eur);
      setIsOwner(response.data.is_owner);
    } catch (error) {
      toast.error('Chyba pri načítaní portfolia');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchPortfolio();
  }, [fetchPortfolio]);

  // Debounced coin search
  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await api.get('/crypto/search', { params: { q: searchQuery } });
        setSearchResults(response.data);
      } catch (error) {
        console.error('Search error:', error);
      } finally {
        setSearching(false);
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSelectCoin = (coin) => {
    setSelectedCoin(coin);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleAddHolding = async (e) => {
    e.preventDefault();
    if (!selectedCoin || !amount) return;
    try {
      await api.post('/crypto/holdings', {
        coin_id: selectedCoin.id,
        coin_name: selectedCoin.name,
        coin_symbol: selectedCoin.symbol,
        amount: parseFloat(amount)
      });
      toast.success(`${selectedCoin.name} pridaný do portfolia`);
      setSelectedCoin(null);
      setAmount('');
      setShowAddForm(false);
      fetchPortfolio();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Chyba pri pridávaní');
    }
  };

  const handleUpdateHolding = async (id) => {
    if (!editAmount) return;
    try {
      await api.put(`/crypto/holdings/${id}`, { amount: parseFloat(editAmount) });
      toast.success('Množstvo aktualizované');
      setEditingId(null);
      setEditAmount('');
      fetchPortfolio();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Chyba pri úprave');
    }
  };

  const handleDeleteHolding = async (id, coinName) => {
    if (!window.confirm(`Naozaj chcete odstrániť ${coinName}?`)) return;
    try {
      await api.delete(`/crypto/holdings/${id}`);
      toast.success(`${coinName} odstránený`);
      fetchPortfolio();
    } catch (error) {
      toast.error('Chyba pri mazaní');
    }
  };

  const startEdit = (holding) => {
    setEditingId(holding.id);
    setEditAmount(String(holding.amount));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditAmount('');
  };

  const formatEur = (value) => {
    return new Intl.NumberFormat('sk-SK', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  };

  const formatPrice = (value) => {
    if (value >= 1) {
      return new Intl.NumberFormat('sk-SK', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    }
    return new Intl.NumberFormat('sk-SK', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    }).format(value);
  };

  if (loading) {
    return (
      <div className="crypto-container">
        <div className="crypto-loading">Načítavam portfolio...</div>
      </div>
    );
  }

  return (
    <div className="crypto-container">
      <div className="crypto-header">
        <div className="crypto-title">
          <FaBitcoin className="crypto-title-icon" />
          <h1>Crypto Portfolio</h1>
        </div>
        <div className="crypto-header-actions">
          <motion.button
            className="crypto-btn crypto-btn-refresh"
            onClick={() => fetchPortfolio(true)}
            disabled={refreshing}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <FaSync className={refreshing ? 'spinning' : ''} />
            Obnoviť ceny
          </motion.button>
          {isOwner && <motion.button
            className="crypto-btn crypto-btn-add"
            onClick={() => setShowAddForm(!showAddForm)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {showAddForm ? <FaTimes /> : <FaPlus />}
            {showAddForm ? 'Zrušiť' : 'Pridať coin'}
          </motion.button>}
        </div>
      </div>

      {/* Total Portfolio Value */}
      <motion.div
        className="crypto-total-card"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <span className="crypto-total-label">Celková hodnota portfolia</span>
        <span className="crypto-total-value">{formatEur(totalValue)}</span>
      </motion.div>

      {/* Add Coin Form */}
      <AnimatePresence>
        {showAddForm && (
          <motion.div
            className="crypto-add-form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
          >
            <form onSubmit={handleAddHolding}>
              <div className="crypto-form-row">
                <div className="crypto-search-wrapper">
                  <FaSearch className="crypto-search-icon" />
                  {selectedCoin ? (
                    <div className="crypto-selected-coin">
                      {selectedCoin.thumb && (
                        <img src={selectedCoin.thumb} alt={selectedCoin.name} className="crypto-coin-thumb" />
                      )}
                      <span>{selectedCoin.name} ({selectedCoin.symbol})</span>
                      <button type="button" className="crypto-clear-coin" onClick={() => setSelectedCoin(null)}>
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <input
                      type="text"
                      placeholder="Vyhľadať kryptomenu..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="crypto-search-input"
                    />
                  )}
                  {/* Search Results Dropdown */}
                  {searchResults.length > 0 && !selectedCoin && (
                    <div className="crypto-search-dropdown">
                      {searchResults.map(coin => (
                        <div
                          key={coin.id}
                          className="crypto-search-item"
                          onClick={() => handleSelectCoin(coin)}
                        >
                          {coin.thumb && (
                            <img src={coin.thumb} alt={coin.name} className="crypto-coin-thumb" />
                          )}
                          <span className="crypto-search-name">{coin.name}</span>
                          <span className="crypto-search-symbol">{coin.symbol}</span>
                          {coin.market_cap_rank && (
                            <span className="crypto-search-rank">#{coin.market_cap_rank}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {searching && (
                    <div className="crypto-search-dropdown">
                      <div className="crypto-search-item">Hľadám...</div>
                    </div>
                  )}
                </div>
                <input
                  type="number"
                  step="any"
                  min="0"
                  placeholder="Množstvo"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="crypto-amount-input"
                  required
                />
                <motion.button
                  type="submit"
                  className="crypto-btn crypto-btn-submit"
                  disabled={!selectedCoin || !amount}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FaPlus /> Pridať
                </motion.button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Holdings Table */}
      {holdings.length === 0 ? (
        <div className="crypto-empty">
          <FaBitcoin className="crypto-empty-icon" />
          <p>Zatiaľ žiadne kryptomeny v portfoliu</p>
          <p className="crypto-empty-hint">Klikni na "Pridať coin" a začni sledovať svoje investície</p>
        </div>
      ) : (
        <div className="crypto-table-wrapper">
          <table className="crypto-table">
            <thead>
              <tr>
                <th>Coin</th>
                <th className="text-right">Množstvo</th>
                <th className="text-right">Cena (EUR)</th>
                <th className="text-right">24h</th>
                <th className="text-right">Hodnota (EUR)</th>
                {isOwner && <th className="text-center">Akcie</th>}
              </tr>
            </thead>
            <tbody>
              <AnimatePresence>
                {holdings.map(holding => (
                  <motion.tr
                    key={holding.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                  >
                    <td className="crypto-coin-cell">
                      <span className="crypto-coin-name">{holding.coin_name}</span>
                      <span className="crypto-coin-symbol">{holding.coin_symbol}</span>
                    </td>
                    <td className="text-right">
                      {isOwner && editingId === holding.id ? (
                        <div className="crypto-edit-inline">
                          <input
                            type="number"
                            step="any"
                            min="0"
                            value={editAmount}
                            onChange={(e) => setEditAmount(e.target.value)}
                            className="crypto-edit-input"
                            autoFocus
                          />
                          <button className="crypto-inline-btn save" onClick={() => handleUpdateHolding(holding.id)}>
                            <FaCheck />
                          </button>
                          <button className="crypto-inline-btn cancel" onClick={cancelEdit}>
                            <FaTimes />
                          </button>
                        </div>
                      ) : (
                        <span>{holding.amount}</span>
                      )}
                    </td>
                    <td className="text-right">{formatPrice(holding.price_eur)}</td>
                    <td className={`text-right crypto-change ${(holding.price_change_24h || 0) >= 0 ? 'positive' : 'negative'}`}>
                      {(holding.price_change_24h || 0) >= 0 ? <FaArrowUp /> : <FaArrowDown />}
                      {Math.abs(holding.price_change_24h || 0).toFixed(2)}%
                    </td>
                    <td className="text-right crypto-value">{formatEur(holding.value_eur)}</td>
                    {isOwner && <td className="text-center">
                      <div className="crypto-actions">
                        <button
                          className="crypto-action-btn edit"
                          onClick={() => startEdit(holding)}
                          title="Upraviť množstvo"
                        >
                          <FaEdit />
                        </button>
                        <button
                          className="crypto-action-btn delete"
                          onClick={() => handleDeleteHolding(holding.id, holding.coin_name)}
                          title="Odstrániť"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>}
                  </motion.tr>
                ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CryptoPortfolio;
